/**
 * Helpers for the multi-source `lookup_map` mapping editor.
 *
 * For multi-source `lookup_map` rules the engine expects a mapping that is
 * nested one level per source column. The editor flattens that nested object
 * into one row per leaf path so the user can edit each combination directly,
 * and rebuilds the nested object before persisting.
 *
 * The wildcard key `*` matches any value of its source that is not explicitly
 * listed elsewhere; the engine resolves it during lookup.
 */

export interface MultiSourceMappingLeaf {
  /** One entry per source column, in the same order as the rule's `sources`. */
  keys: string[]
  /** The target value to assign when all keys match. */
  value: string
}

/**
 * Walk a (possibly nested) mapping and emit one leaf per terminal path of the
 * given depth. When the JSON disagrees with the current source count we pad or
 * stringify defensively so the editor still renders something useful instead
 * of crashing or showing `[object Object]`.
 */
export function flattenMultiMapping(
  mapping: Record<string, unknown> | undefined,
  depth: number
): MultiSourceMappingLeaf[] {
  if (!mapping || depth <= 0) return []
  // A top-level empty object means "no rules yet"; show an empty table so the
  // user starts from a clean slate instead of a stray placeholder row.
  if (Object.keys(mapping).length === 0) return []

  const out: MultiSourceMappingLeaf[] = []

  const walk = (node: unknown, path: string[]): void => {
    if (path.length >= depth) {
      out.push({
        keys: path,
        value:
          typeof node === 'string'
            ? node
            : node == null
              ? ''
              : JSON.stringify(node),
      })
      return
    }

    if (node && typeof node === 'object' && !Array.isArray(node)) {
      const obj = node as Record<string, unknown>
      const objKeys = Object.keys(obj)
      if (objKeys.length === 0) {
        const padded = [...path, ...Array(depth - path.length).fill('')]
        out.push({ keys: padded, value: '' })
        return
      }
      for (const k of objKeys) {
        walk(obj[k], [...path, k])
      }
      return
    }

    // Leaf surfaced before the expected depth – pad with empty placeholders so
    // the row still has the right number of source-key cells.
    const padded = [...path, ...Array(depth - path.length).fill('')]
    out.push({
      keys: padded,
      value: typeof node === 'string' ? node : node == null ? '' : String(node),
    })
  }

  walk(mapping, [])
  return out
}

/**
 * Rebuild a nested mapping from the flattened editor representation. Entries
 * with the same key path collide with last-write-wins semantics, mirroring how
 * the user perceives the table (each row is a single mapping).
 */
export function buildNestedMapping(
  entries: ReadonlyArray<MultiSourceMappingLeaf>
): Record<string, unknown> {
  const root: Record<string, unknown> = {}
  for (const entry of entries) {
    if (entry.keys.length === 0) continue
    let node: Record<string, unknown> = root
    for (let i = 0; i < entry.keys.length - 1; i++) {
      const k = entry.keys[i]
      const next = node[k]
      if (next && typeof next === 'object' && !Array.isArray(next)) {
        node = next as Record<string, unknown>
      } else {
        const fresh: Record<string, unknown> = {}
        node[k] = fresh
        node = fresh
      }
    }
    node[entry.keys[entry.keys.length - 1]] = entry.value
  }
  return root
}
